import { createSheetEditorValue } from '@gitbook/doc-core';
import type {
    ContentBranch,
    ContentBranchHistory,
    ContentBranchHistoryV2,
    Revision,
    RevisionPageDocument,
    RevisionReusableContent,
} from '@gitbook/hive-core';
import { type Document, type DocumentJSON, deserialize, serialize } from '@gitbook/slate';
import {
    createCache,
    createCacheMap,
    type MaybePromise,
    maybePromiseAll,
    type SuspenseCache,
    type SuspenseCacheMap,
    suspenseToMaybePromise,
    thenMaybePromise,
} from '@gitbook/util-suspense-cache';

import type { HiveClient } from './HiveClient';

export type HiveContentStoreOptions = {
    /**
     * Underlying memory cache to store the revision.
     */
    revisionMemoryCache?: SuspenseCacheMap;
    /**
     * Underlying memory cache to store the branch.
     */
    branchMemoryCache?: SuspenseCacheMap;
    /**
     * Underlying memory cache to store the document.
     */
    documentMemoryCache?: SuspenseCacheMap;
    /**
     * Underlying memory cache to store the branch history.
     */
    branchHistoryMemoryCache?: SuspenseCacheMap;
};

/**
 * Re-export the createCacheMap function to ease the creation of a map for the store.
 */
export const createHiveContentStoreMap = createCacheMap;

/**
 * HiveContentStore stores and cache content (revision and documents) to be accessed multiple times.
 * The store is designed to be used in React component (suspending rendering) or in async functions.
 */
export class HiveContentStore {
    private client: HiveClient;

    private branchesCache: SuspenseCache<
        {
            contentId: string;
            branchId: string;
        },
        ContentBranch | undefined
    >;

    private revisionsCache: SuspenseCache<
        {
            contentId: string;
            revisionId: string;
            metadata: boolean;
        },
        Revision | undefined
    >;

    private branchesHistoryCache: SuspenseCache<
        {
            contentId: string;
            branchId: string;
            initialRevisionId: string;
            size: number;
            git?: boolean;
        },
        ContentBranchHistory | ContentBranchHistoryV2
    >;

    private documentsCache: SuspenseCache<
        {
            contentId: string;
            documentId: string;
        },
        Document | undefined
    >;

    constructor(client: HiveClient, options: HiveContentStoreOptions = {}) {
        const {
            revisionMemoryCache = createCacheMap({ maxSize: 500 }),
            branchMemoryCache = createCacheMap({ maxSize: 1000 }),
            documentMemoryCache = createCacheMap({ maxSize: 500 }),
            branchHistoryMemoryCache = createCacheMap({ maxSize: 500 }),
        } = options;

        this.client = client;

        this.branchesCache = createCache({
            getKey: (input) => `${input.contentId}:${input.branchId}`,
            getValue: async (input) => {
                try {
                    const revision = await this.client.getBranch(input.contentId, input.branchId);
                    return revision;
                } catch (error) {
                    if ((error as { statusCode?: number }).statusCode === 404) {
                        return undefined;
                    }

                    throw error;
                }
            },
            map: branchMemoryCache,
        });

        this.revisionsCache = createCache({
            getKey: (input) => `${input.contentId}:${input.revisionId}:${input.metadata}`,
            getValue: async (input) => {
                try {
                    const revision = await this.client.getRevision(
                        input.contentId,
                        input.revisionId,
                        {
                            metadata: input.metadata,
                        }
                    );
                    return revision;
                } catch (error) {
                    if ((error as { statusCode?: number }).statusCode === 404) {
                        return undefined;
                    }

                    throw error;
                }
            },
            map: revisionMemoryCache,
        });

        this.branchesHistoryCache = createCache({
            getKey: (input) =>
                `${input.contentId}:${input.branchId}:${input.initialRevisionId}:${input.size}:${
                    input.git ?? false
                }`,
            getValue: async (input) => {
                const history = await this.client.getBranchHistory(
                    input.contentId,
                    input.branchId,
                    {
                        page: input.initialRevisionId,
                        limit: input.size,
                        ...(input.git !== undefined ? { git: input.git } : {}),
                    }
                );
                return history;
            },
            map: branchHistoryMemoryCache,
        });

        this.documentsCache = createCache({
            getKey: (input) => `${input.contentId}:${input.documentId}`,
            getValue: async (input) => {
                try {
                    const document = await this.client.getDocument(
                        input.contentId,
                        input.documentId
                    );
                    return document;
                } catch (error) {
                    if ((error as { statusCode?: number }).statusCode === 404) {
                        return undefined;
                    }

                    throw error;
                }
            },
            toJS: (value: Document | undefined) => {
                return value !== undefined ? serialize(value) : 'undefined';
            },
            fromJS: (value: any) => {
                return value !== 'undefined' ? deserialize(value as DocumentJSON) : undefined;
            },
            map: documentMemoryCache,
        });
    }

    /**
     * Get the history of a branch.
     */
    public getBranchHistory(
        contentId: string,
        branchId: string,
        options: {
            page: Revision['id'];
            limit: number;
            git?: boolean;
        }
    ): MaybePromise<ContentBranchHistory | ContentBranchHistoryV2> {
        return suspenseToMaybePromise(
            this.branchesHistoryCache.readCacheState({
                contentId,
                branchId,
                initialRevisionId: options.page,
                size: options.limit,
                ...(options.git !== undefined ? { git: options.git } : {}),
            })
        );
    }

    /**
     * Read branch metadata, preferring an existing branch sync's working revision.
     * Otherwise, read the persisted branch head through the store cache. This does not open a sync,
     * force a save, or refresh cached data; it is not a guarantee of the latest server state.
     * Read individual pages with getBranchPageDocument() to include their existing live edits.
     */
    public getBranchRevision(
        contentId: string,
        branchId: string,
        options: {
            /**
             * If false, the source revision will be returned instead of the computed revision.
             */
            computed?: boolean;
        } = {}
    ): MaybePromise<Revision | undefined> {
        return thenMaybePromise(
            this.getBranchRevisions(contentId, branchId, options),
            (result) => result?.revision
        );
    }

    /**
     * Read the source and computed branch revisions with the same freshness as getBranchRevision().
     */
    public getBranchRevisions(
        contentId: string,
        branchId: string,
        options: {
            /**
             * If false, the source revision will be returned instead of the computed revision.
             */
            computed?: boolean;
        } = {}
    ): MaybePromise<{ revision: Revision; sourceRevision: Revision } | undefined> {
        const { computed = this.client.hasContentComputer() } = options;

        // If we have an active sync with this content, we use it instead of fetching.
        const sync = this.client.sync.branch(contentId, branchId, {
            autoInit: false,
        });

        if (sync) {
            return thenMaybePromise(sync.waitForComputed(), () => ({
                revision: computed ? sync.revision : sync.sourceRevision,
                sourceRevision: sync.sourceRevision,
            }));
        }

        return thenMaybePromise(
            suspenseToMaybePromise(
                this.branchesCache.readCacheState({
                    contentId,
                    branchId,
                })
            ),
            (branch: ContentBranch | undefined) => {
                if (branch) {
                    return thenMaybePromise(
                        this.getRevision(contentId, branch.revision),
                        (sourceRevision) => {
                            if (!sourceRevision) {
                                return undefined;
                            }
                            if (!computed) {
                                return { sourceRevision, revision: sourceRevision };
                            }

                            const computer = this.client.getContentComputer(contentId);
                            return thenMaybePromise(
                                computer.getCompleteRevision(sourceRevision),
                                (result) => ({ revision: result.revision, sourceRevision })
                            );
                        }
                    );
                }

                return undefined;
            }
        );
    }

    /**
     * Read a page for current-branch UI, preferring an existing page sync's current document.
     * Pass a page from the same content and branch, typically obtained via getBranchRevision().
     * This does not reload the page metadata or open, wait for, or refresh a sync.
     * Without a page sync, read the supplied page's saved document through the store cache.
     * Computed pages always use their computed/saved document, never the source page's live edits.
     *
     * Use getPageDocument() for revision-pinned reads such as history and diffs. If server freshness
     * is required without an existing sync, explicitly open a sync or use HiveClient.getPageDocument()
     * where supported; this helper alone does not guarantee fresh remote edits.
     *
     * An absent document produces a new empty editor document on each read. By default, the saved
     * fallback also checks branch existence through the cache and returns undefined if it is missing.
     *
     * @example
     * // The caller already resolved this branch and selected the page from its revision.
     * const document = await store.getBranchPageDocument(contentId, branchId, page, {
     *     checkBranchExists: false,
     * });
     */
    public getBranchPageDocument(
        contentId: string,
        branchId: string,
        page: RevisionPageDocument,
        options: {
            /**
             * Check branch existence on the saved-document fallback (default true).
             * Set false only when the caller already resolved the same branch. This skips a possible
             * branch fetch; it does not refresh the supplied page or change document freshness.
             */
            checkBranchExists?: boolean;
        } = {}
    ): MaybePromise<Document | undefined> {
        // If the page is computed, do not use sync, we need to get the computed document.
        if (!page.computed) {
            // If we have an active sync with this page's document, we use it instead of fetching.
            const sync = this.client.sync.pageDocument(contentId, branchId, page.id, {
                autoInit: false,
            });
            if (sync) {
                return sync.value.document;
            }
        }

        const document = thenMaybePromise(
            this.getPageDocument(contentId, page),
            (document) => document ?? createSheetEditorValue(page.id, 'document').document
        );
        if (options.checkBranchExists === false) {
            return document;
        }

        return maybePromiseAll(
            [
                suspenseToMaybePromise(
                    this.branchesCache.readCacheState({
                        contentId,
                        branchId,
                    })
                ),
                document,
            ],
            ([branch, document]) => (branch ? document : undefined)
        );
    }

    /**
     * Get a specific revision in a content.
     * Keep in mind that if it's used to load the currently edited revision,
     * it'll not return it and instead fetch it from the backend (adding latency and potentially returning undefined).
     * Use getBranchRevision() instead.
     */
    public getRevision(
        contentId: string,
        revisionId: string,
        options: {
            /**
             * See `HiveClient.getRevision` for more details.
             */
            metadata: boolean;
        } = {
            metadata: true,
        }
    ): MaybePromise<Revision | undefined> {
        // Test with metadata first to avoid fetching the revision if it's not needed.
        if (!options.metadata) {
            const state = this.revisionsCache.getCacheState({
                contentId,
                revisionId,
                metadata: true,
            });

            if (state?.result) {
                return state.result;
            }
        }

        return suspenseToMaybePromise(
            this.revisionsCache.readCacheState({
                contentId,
                revisionId,
                metadata: options.metadata,
            })
        );
    }

    /**
     * Test if a revision is cached in the store.
     */
    public hasRevision(contentId: string, revisionId: string): boolean {
        const state = this.revisionsCache.getCacheState({
            contentId,
            revisionId,
            metadata: true,
        });

        return state?.result !== undefined;
    }

    /**
     * Get a specific revision in a content.
     * See getRevision() for more details.
     */
    public assertRevision(
        contentId: string,
        revisionId: string,
        options?: {
            /**
             * See `HiveClient.getRevision` for more details.
             */
            metadata: boolean;
        }
    ): MaybePromise<Revision> {
        return thenMaybePromise(
            this.getRevision(contentId, revisionId, options),
            (revision: Revision | undefined) => {
                if (!revision) {
                    throw new Error(`Revision ${revisionId} not found in content ${contentId}`);
                }

                return revision;
            }
        );
    }

    /**
     * Read the saved or computed document described by a page or reusable content, through the cache.
     * This does not consult live edits or refresh the supplied metadata. Use it for revision-pinned
     * reads; use getBranchPageDocument() when current-branch UI should include existing live edits.
     */
    public getPageDocument(
        contentId: string,
        page: RevisionPageDocument | RevisionReusableContent
    ): MaybePromise<Document | undefined> {
        if (page.document) {
            return this.getDocument(contentId, page.document);
        }

        if (page.computed && this.client.hasContentComputer()) {
            const computer = this.client.getContentComputer(contentId);
            return thenMaybePromise(
                computer.getCompleteDocument({
                    computedSeed: page.computed.seed,
                    computedSource: page.computed.source,
                }),
                (result) => result.document
            );
        }
    }
    public getReusableContentDocument = this.getPageDocument;

    /**
     * Read an immutable document ID through the cache, without following a branch or its live edits.
     */
    public getDocument(contentId: string, documentId: string): MaybePromise<Document | undefined> {
        return suspenseToMaybePromise(
            this.documentsCache.readCacheState({
                contentId,
                documentId,
            })
        );
    }

    /**
     * Get a specific document in a content.
     * See getDocument() for more details.
     */
    public assertDocument(contentId: string, documentId: string): MaybePromise<Document> {
        return thenMaybePromise(
            this.getDocument(contentId, documentId),
            (document: Document | undefined) => {
                if (!document) {
                    throw new Error(`Document ${documentId} not found in content ${contentId}`);
                }

                return document;
            }
        );
    }

    /**
     * Reset the store for a revision.
     */
    public clearRevision(contentId: string, revisionId: string) {
        this.revisionsCache.clear({
            contentId,
            revisionId,
            metadata: true,
        });
        this.revisionsCache.clear({
            contentId,
            revisionId,
            metadata: false,
        });
    }

    /**
     * Reset the store for a document.
     */
    public clearDocument(contentId: string, documentId: string) {
        this.documentsCache.clear({
            contentId,
            documentId,
        });
    }

    /**
     * Reset the store for the history of a branch.
     */
    public clearHistoryCache() {
        this.branchesHistoryCache.clearAll();
    }

    /**
     * Serialize the store to a JSON object.
     */
    public toJSON() {
        return {
            documents: this.documentsCache.toJS(),
            revisions: this.revisionsCache.toJS(),
            branches: this.branchesCache.toJS(),
        };
    }

    /**
     * Load the store from a JSON object.
     */
    public fromJSON(json: any) {
        if (json.documents) {
            this.documentsCache.fromJS(json.documents);
        }
        if (json.revisions) {
            this.revisionsCache.fromJS(json.revisions);
        }
        if (json.branches) {
            this.branchesCache.fromJS(json.branches);
        }
    }
}
